import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_SPORT_CODES } from '@low6-pickem/sports-config';

export type SportFilterValue = 'ALL' | (typeof SUPPORTED_SPORT_CODES)[number];

interface SportFilterProps {
  selectedSport: SportFilterValue;
  onSportChange: (sport: SportFilterValue) => void;
}

interface SportOption {
  value: SportFilterValue;
  label: string;
  svgSrc: string;
}

/** Sport filters not needed for CD single-sport lobby — keep code for later. */
const SHOW_SPORT_FILTERS = false;

/**
 * Lobby section heading (+ optional sport filter).
 * Figma 10136:15995 — "Select a contest" header-03, filters hidden.
 */
const SportFilter: React.FC<SportFilterProps> = ({ selectedSport, onSportChange }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isFiltered = selectedSport !== 'ALL';

  const sportOptions: SportOption[] = [
    { value: 'ALL', label: t('filter.all'), svgSrc: '/all-sports-icon.svg' },
    { value: 'NBA', label: 'NBA', svgSrc: '/basketball-icon.svg' },
    { value: 'NFL', label: 'NFL', svgSrc: '/football-icon.svg' },
    { value: 'NCAAMB', label: 'NCAAB', svgSrc: '/basketball-icon.svg' },
  ];

  return (
    <div>
      <div
        className={`relative flex items-center justify-center ${SHOW_SPORT_FILTERS ? 'mb-2' : ''}`}
      >
        <h2 className="text-center font-sans text-header-03 text-white">{t('pages.gamesCount')}</h2>
        {SHOW_SPORT_FILTERS && (
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            className={`absolute right-0 rounded-lg p-2 transition-colors ${
              isExpanded || isFiltered ? 'text-primary' : 'text-muted-foreground hover:text-white'
            }`}
            aria-label={t('navigation.filter')}
            aria-expanded={isExpanded}
          >
            <ListFilter size={20} />
          </button>
        )}
      </div>

      {SHOW_SPORT_FILTERS && (
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="flex justify-center gap-2 overflow-x-auto rounded-lg bg-white/5 px-3 py-2.5 scrollbar-hide">
                {sportOptions.map(option => {
                  const isActive = selectedSport === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => onSportChange(option.value)}
                      className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                        isActive
                          ? 'border-white bg-white text-background'
                          : 'border-muted-foreground/40 bg-transparent text-muted-foreground hover:border-muted-foreground'
                      }`}
                    >
                      <img
                        src={option.svgSrc}
                        alt={option.label}
                        className={`h-4 w-4 ${isActive ? 'brightness-0' : 'opacity-70'}`}
                      />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default SportFilter;
